/**
 * Detail collapse for an establishment.
 */
import * as React from 'react';
import {
  Building2,
  MapPin,
  Phone,
  Mail,
  User,
  Users,
  Minus,
  DoorOpen,
  UserCog,
  Shield,
  Clock,
  Handshake,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { DetailBadge } from '@/components/common/details/DetailBadge';
import { EmptyPlaceholder } from '@/components/common/details/EmptyPlaceholder';
import { DetailField } from '@/components/common/details/DetailField';
import { DetailChipList } from '@/components/common/details/DetailChipList';
import { DetailsCard } from '@/components/common/DetailsCard';
import {
  ESTABLISHMENT_PRIORITY_NETWORK_CONFIG,
  ESTABLISHMENT_STATUS_CONFIG,
  PARTNER_RELATIONSHIP_QUALITY_CONFIG,
  ENGAGEMENT_FIELD_LABELS,
  ESTABLISHMENT_TYPE_CONFIG,
} from '@/components/establishments/establishment-configs';
import {
  computePartnershipSeniority,
  formatEstablishmentDepartment,
  formatIpsValue,
  formatQpvLabel,
  getEstablishmentStatusColor,
} from '@/components/establishments/establishment-table-utils';
import { formatPersonName } from '@/components/establishments/establishment-form-utils';
import { getClassrooms } from '@/services/establishments.service';
import type { Establishment } from '@/types/establishment.types';

/** Props for {@link ContactsSection}. */
interface ContactListProps {
  establishment: Establishment;
}

/** Prefer `contacts[]`; fall back to legacy contactName / email / phone fields. */
function ContactsSection({ establishment }: Readonly<ContactListProps>) {
  const hasContacts = establishment.contacts && establishment.contacts.length > 0;
  const hasLegacyContact =
    establishment.contactName || establishment.contactEmail || establishment.contactPhone;

  if (hasContacts) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        {establishment.contacts.map((contact, index) => (
          <div
            key={contact.contactEmail || contact.contactName || `contact-${index}`}
            className="p-3 rounded-lg border border-[color-mix(in_srgb,var(--color-3-nuance-1)_20%,transparent)] bg-[color-mix(in_srgb,var(--color-3-nuance-1)_5%,transparent)]"
          >
            <div className="space-y-2">
              {contact.contactName && (
                <div className="flex items-center gap-2">
                  <User className="w-3.5 h-3.5 text-[var(--color-3-nuance-1)] shrink-0" />
                  <span className="text-sm font-medium text-foreground truncate">
                    {contact.contactName}
                  </span>
                </div>
              )}
              {contact.contactEmail && (
                <div className="flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5 text-[var(--color-3-nuance-1)] shrink-0" />
                  <span className="text-xs text-muted-foreground truncate">
                    {contact.contactEmail}
                  </span>
                </div>
              )}
              {contact.contactPhone && (
                <div className="flex items-center gap-2">
                  <Phone className="w-3.5 h-3.5 text-[var(--color-3-nuance-1)] shrink-0" />
                  <span className="text-xs text-muted-foreground">{contact.contactPhone}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (hasLegacyContact) {
    return (
      <div className="grid grid-cols-3 gap-4">
        {establishment.contactName && (
          <DetailField icon={User} label="Nom" value={establishment.contactName} />
        )}
        {establishment.contactEmail && (
          <DetailField icon={Mail} label="Email" value={establishment.contactEmail} />
        )}
        {establishment.contactPhone && (
          <DetailField icon={Phone} label="Téléphone" value={establishment.contactPhone} />
        )}
      </div>
    );
  }

  return <EmptyPlaceholder message="Aucun CPE renseigné" />;
}

/** Expanded-row / details-modal body for one establishment. */
const EstablishmentDetails = ({ establishment }: { establishment: Establishment }) => {
  const statusConfig =
    ESTABLISHMENT_STATUS_CONFIG[establishment.status] || ESTABLISHMENT_STATUS_CONFIG.active;
  const StatusIcon = statusConfig.icon;

  const typeConfig = establishment.type ? ESTABLISHMENT_TYPE_CONFIG[establishment.type] : null;
  const TypeIcon = typeConfig?.icon;

  const { data: classrooms = [] } = useQuery({
    queryKey: ['classrooms', establishment.id],
    queryFn: () => getClassrooms(establishment.id),
  });
  const typeColorName = typeConfig?.colorName ?? 'color-2';
  const seniority = computePartnershipSeniority(establishment.engagementStartDate);

  return (
    <>
      <div className="details-view-header">
        <div className="details-view-header-left">
          <div className="details-view-header-icon">
            <Building2 className="w-6 h-6 text-[var(--color-2-nuance-1)]" />
          </div>
          <div className="details-view-header-content">
            <div className="details-view-header-with-badges">
              <h3 className="details-view-header-title">{establishment.name}</h3>
              {typeConfig && TypeIcon && (
                <DetailBadge
                  label={typeConfig.label}
                  icon={TypeIcon}
                  colorName={typeColorName}
                  variant="filled"
                />
              )}
              <DetailBadge
                label={statusConfig.label}
                icon={StatusIcon}
                colorName={getEstablishmentStatusColor(establishment.status)}
                variant="outlined"
              />
            </div>
            {establishment.officialName && (
              <p className="details-view-header-subtitle">{establishment.officialName}</p>
            )}
          </div>
        </div>
      </div>

      <div className="details-view-grid">
        <DetailsCard
          icon={Building2}
          title="Informations générales"
          colorName="color-2"
          colSpan={1}
        >
          <div className="space-y-3">
            <DetailField label="Code UAI" value={establishment.uaiCode} />
            <DetailField label="Nom" value={establishment.name} />
            {establishment.officialName && (
              <DetailField label="Nom officiel" value={establishment.officialName} />
            )}
            {establishment.ipsValue != null && (
              <DetailField
                label="IPS"
                value={formatIpsValue(establishment.ipsValue, establishment.ipsYear)}
              />
            )}
            {establishment.priorityNetwork && (
              <DetailField
                label="Réseau prioritaire"
                value={
                  ESTABLISHMENT_PRIORITY_NETWORK_CONFIG[establishment.priorityNetwork]?.label ??
                  establishment.priorityNetwork
                }
              />
            )}
            {establishment.isQpv != null && (
              <DetailField label="QPV" value={formatQpvLabel(establishment.isQpv)} />
            )}
          </div>
        </DetailsCard>

        <DetailsCard icon={MapPin} title="Adresse" colorName="color-1" colSpan={1}>
          {establishment.addressLine1 || establishment.postalCode || establishment.city ? (
            <div className="space-y-3">
              {establishment.addressLine1 && (
                <DetailField label="Adresse" value={establishment.addressLine1} />
              )}
              {(establishment.department || establishment.departmentCode) && (
                <DetailField
                  label="Département"
                  value={formatEstablishmentDepartment(
                    establishment.department,
                    establishment.departmentCode,
                  )}
                />
              )}
              {establishment.postalCode && establishment.city && (
                <DetailField
                  label="Ville"
                  value={`${establishment.postalCode} ${establishment.city}`}
                />
              )}
            </div>
          ) : (
            <EmptyPlaceholder message="Aucune adresse renseignée" />
          )}
        </DetailsCard>

        <DetailsCard icon={Users} title="Engagement & Contrat" colorName="color-2" colSpan={1}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {establishment.studentQuota !== null && establishment.studentQuota !== undefined && (
              <DetailField
                label={ENGAGEMENT_FIELD_LABELS.studentsToAccompany}
                value={establishment.studentQuota.toString()}
              />
            )}
            {establishment.volunteerQuota !== null &&
              establishment.volunteerQuota !== undefined && (
                <DetailField
                  label={ENGAGEMENT_FIELD_LABELS.vscToRecruit}
                  value={establishment.volunteerQuota.toString()}
                />
              )}
            {establishment.volunteersToRecruit !== null &&
              establishment.volunteersToRecruit !== undefined && (
                <DetailField
                  label={ENGAGEMENT_FIELD_LABELS.volunteersToRecruit}
                  value={establishment.volunteersToRecruit.toString()}
                />
              )}
            {establishment.tutorCount !== null && establishment.tutorCount !== undefined && (
              <DetailField
                label={ENGAGEMENT_FIELD_LABELS.volunteersPerEstablishment}
                value={establishment.tutorCount.toString()}
              />
            )}
            {establishment.engagementStartDate && (
              <DetailField
                label="Date de début partenariat"
                value={new Date(establishment.engagementStartDate).toLocaleDateString('fr-FR')}
              />
            )}
            {establishment.engagementEndDate && (
              <DetailField
                label="Date fin partenariat"
                value={new Date(establishment.engagementEndDate).toLocaleDateString('fr-FR')}
              />
            )}
            {seniority && (
              <DetailField icon={Clock} label="Ancienneté partenariat" value={seniority} />
            )}
            {establishment.renewalNextYear != null && (
              <DetailField
                label="Reconduction prévue N+1"
                value={establishment.renewalNextYear ? 'Oui' : 'Non'}
              />
            )}
          </div>
        </DetailsCard>

        {(establishment.conventionSigned != null ||
          establishment.hasDevoirsFaits != null ||
          establishment.hasInternat != null ||
          establishment.involvementLevel != null ||
          establishment.partnerRelationshipQuality != null) && (
          <DetailsCard
            icon={Handshake}
            title="Dispositifs & Partenariat"
            colorName="color-3"
            colSpan={1}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {establishment.conventionSigned != null && (
                <DetailField
                  label="Convention signée"
                  value={establishment.conventionSigned ? 'Oui' : 'Non'}
                />
              )}
              {establishment.hasDevoirsFaits != null && (
                <DetailField
                  label="Présence dispositif Devoirs Faits"
                  value={establishment.hasDevoirsFaits ? 'Oui' : 'Non'}
                />
              )}
              {establishment.hasInternat != null && (
                <DetailField
                  label="Présence internat"
                  value={establishment.hasInternat ? 'Oui' : 'Non'}
                />
              )}
              {establishment.involvementLevel != null && (
                <DetailField
                  label="Niveau d'implication"
                  value={`${establishment.involvementLevel} / 5`}
                />
              )}
              {establishment.partnerRelationshipQuality != null && (
                <DetailField
                  label="Qualité relation partenaire"
                  value={
                    PARTNER_RELATIONSHIP_QUALITY_CONFIG[establishment.partnerRelationshipQuality]
                      ?.label ?? establishment.partnerRelationshipQuality
                  }
                />
              )}
            </div>
          </DetailsCard>
        )}

        <DetailsCard icon={User} title="CPE" colorName="color-3" colSpan="full">
          <ContactsSection establishment={establishment} />
        </DetailsCard>

        <DetailsCard icon={DoorOpen} title="Salles" colorName="color-1" colSpan={1}>
          <DetailField label="Nombre de salles" value={classrooms.length.toString()} />
        </DetailsCard>

        <DetailsCard
          icon={UserCog}
          title="Gestion de l'établissement"
          colorName="color-2"
          colSpan={2}
        >
          <div className="space-y-6">
            {establishment.managedBy && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Shield className="w-4 h-4 text-[var(--color-2-nuance-1)]" />
                  <h5 className="text-sm font-medium text-foreground">Manager</h5>
                </div>
                <div className="p-3 rounded-lg border border-[color-mix(in_srgb,var(--color-2-nuance-1)_20%,transparent)] bg-[color-mix(in_srgb,var(--color-2-nuance-1)_5%,transparent)]">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-[var(--color-2-nuance-1)] shrink-0" />
                    <span className="text-sm font-medium text-foreground">
                      {formatPersonName(establishment.managedBy)}
                    </span>
                  </div>
                </div>
              </div>
            )}
            {establishment.coordinators && establishment.coordinators.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-[var(--color-2-nuance-1)]" />
                  <h5 className="text-sm font-medium text-foreground">Coordinateurs</h5>
                </div>
                <DetailChipList
                  items={establishment.coordinators.map((c) => ({
                    id: c.id,
                    label: formatPersonName(c),
                    icon: User,
                  }))}
                  colorScheme="color-2"
                  variant="filled"
                />
              </div>
            )}
            {!establishment.managedBy &&
              (!establishment.coordinators || establishment.coordinators.length === 0) && (
                <EmptyPlaceholder message="Aucun gestionnaire assigné" />
              )}
          </div>
        </DetailsCard>
      </div>

      {establishment.createdAt && (
        <div className="mt-8 pt-4 border-t border-[color-mix(in_srgb,var(--foreground)_10%,transparent)]">
          <div className="flex items-center gap-6 text-xs text-muted-foreground">
            <span>Créé le {new Date(establishment.createdAt).toLocaleDateString('fr-FR')}</span>
            {establishment.updatedAt && (
              <>
                <Minus className="w-3 h-3" />
                <span>
                  Modifié le {new Date(establishment.updatedAt).toLocaleDateString('fr-FR')}
                </span>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

/** Details renderer passed to the establishments {@link DataTable}. */
export const renderEstablishmentDetails = (establishment: Establishment): React.ReactNode => {
  return <EstablishmentDetails establishment={establishment} />;
};
